// Render one source line as HTML: highlight spans (`syn-<class>`, plus
// `syn-in-<class>` inside an embedding region) with intraline emphasis
// (`<mark>`) layered on top.
import type { Emphasis, Span } from '../engine/types';

export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/** The CSS classes of a highlight span. */
export function synClasses(span: Span): string {
  return span.within ? `syn-${span.class} syn-in-${span.within}` : `syn-${span.class}`;
}

export function renderLine(text: string, spans: readonly Span[] = [], emphasis: readonly Emphasis[] = []): string {
  if (!spans.length && !emphasis.length) return esc(text);
  const cuts = new Set<number>([0, text.length]);
  for (const s of spans) {
    cuts.add(Math.max(0, Math.min(text.length, s.start)));
    cuts.add(Math.max(0, Math.min(text.length, s.end)));
  }
  for (const e of emphasis) {
    cuts.add(Math.max(0, Math.min(text.length, e.start)));
    cuts.add(Math.max(0, Math.min(text.length, e.end)));
  }
  const points = [...cuts].sort((a, b) => a - b);
  let html = '';
  let open = false;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (a === b) continue;
    const marked = emphasis.some((e) => e.start <= a && b <= e.end);
    if (marked && !open) {
      html += '<mark>';
      open = true;
    } else if (!marked && open) {
      html += '</mark>';
      open = false;
    }
    const span = spans.find((s) => s.start <= a && b <= s.end);
    const piece = esc(text.slice(a, b));
    html += span ? `<span class="${synClasses(span)}">${piece}</span>` : piece;
  }
  if (open) html += '</mark>';
  return html;
}
